import { HTMLAttributes } from 'react';
import {
  AiFillBell,
  AiFillCheckCircle,
  AiFillInfoCircle,
  AiFillInstagram,
  AiFillPrinter,
  AiOutlineContainer,
  AiOutlineDown,
  AiOutlineMenu,
  AiOutlineMinus,
  AiOutlinePlus,
  AiTwotoneEnvironment,
} from 'react-icons/ai';
import { BiErrorCircle, BiSolidError } from 'react-icons/bi';
import { BsPauseBtn, BsPlayBtn, BsSearch, BsTwitterX } from 'react-icons/bs';
import {
  FaCommentsDollar,
  FaEye,
  FaEyeSlash,
  FaFilePrescription,
  FaListUl,
  FaRegAddressCard,
  FaUserCircle,
  FaApple,
  FaGoogle,
} from 'react-icons/fa';
import { FiCheck, FiSliders } from 'react-icons/fi';
import { GrCaretUpFill } from 'react-icons/gr';
import { PiCalendarPlusBold, PiFolderPlusBold } from 'react-icons/pi';
import { HiOutlineExternalLink, HiOutlineX } from 'react-icons/hi';
import { HiChevronUpDown } from 'react-icons/hi2';
import {
  IoIosArrowDown,
  IoIosArrowUp,
  IoIosLock,
  IoMdAddCircle,
  IoMdCheckmarkCircle,
  IoMdHeart,
  IoMdHeartEmpty,
  IoMdInformationCircle,
  IoMdRemoveCircle,
  IoMdVideocam,
} from 'react-icons/io';
import { IconType } from 'react-icons/lib';

import { MdLocationOn, MdOutlineShoppingCart, MdShoppingCart } from 'react-icons/md';

import { GoCheckCircleFill, GoCircle } from 'react-icons/go';
import {
  RiAlertFill,
  RiArrowDownSLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiArrowUpSLine,
  RiErrorWarningFill,
} from 'react-icons/ri';
import { TbFileDollar, TbMap, TbPointFilled } from 'react-icons/tb';
import { FaLocationPin, FaYahoo } from 'react-icons/fa6';
import { LuArrowRightFromLine } from 'react-icons/lu';
import { TiArrowSortedDown } from 'react-icons/ti';
import { DataAnalytics, DataAnalyticsClicks, DataAnalyticsTypes } from './analytics';
import { buildAnalyticsLocations } from './quickLink';
import useSplunkLogger from '../../common/hooks/useSplunkLogger';

export type IconName =
  | 'mapPin'
  | 'heart'
  | 'user'
  | 'logout'
  | 'cart'
  | 'emptyCart'
  | 'search'
  | 'marker'
  | 'menu'
  | 'close'
  | 'caretDown'
  | 'instagram'
  | 'twitterx'
  | 'account'
  | 'bell'
  | 'orders'
  | 'benefits'
  | 'prescriptions'
  | 'favorites'
  | 'sliders'
  | 'downArrow'
  | 'upArrow'
  | 'rightArrow'
  | 'leftArrow'
  | 'dropdownArrow'
  | 'arrowUp'
  | 'favoritesFull'
  | 'quotes'
  | 'videocam'
  | 'info'
  | 'check'
  | 'location'
  | 'tooltipCaret'
  | 'notificationInfo'
  | 'empty'
  | 'plus'
  | 'minus'
  | 'warning'
  | 'visible'
  | 'apple'
  | 'google'
  | 'hidden'
  | 'idCard'
  | 'errorTriangular'
  | 'errorCircle'
  | 'checkCircle'
  | 'addCircle'
  | 'removeCircle'
  | 'checkoutCheckCircle'
  | 'regCircle'
  | 'regCheckCircle'
  | 'chevronUpDown'
  | 'print'
  | 'map'
  | 'play'
  | 'pause'
  | 'dot'
  | 'lock'
  | 'calendar'
  | 'saveLater'
  | 'errWarning'
  | 'externalLink'
  | 'signout'
  | 'sortedArrow'
  | 'yahoo'
  | 'signIn';

const iconMap: Record<IconName, IconType> = {
  mapPin: FaLocationPin,
  heart: IoMdHeartEmpty,
  user: FaUserCircle,
  logout: FaUserCircle,
  cart: MdShoppingCart,
  emptyCart: MdOutlineShoppingCart,
  search: BsSearch,
  marker: AiTwotoneEnvironment,
  menu: AiOutlineMenu,
  close: HiOutlineX,
  caretDown: AiOutlineDown,
  instagram: AiFillInstagram,
  twitterx: BsTwitterX,
  videocam: IoMdVideocam,
  bell: AiFillBell,
  account: FaUserCircle,
  orders: FaListUl,
  benefits: TbFileDollar,
  prescriptions: FaFilePrescription,
  favorites: IoMdHeartEmpty,
  sliders: FiSliders,
  downArrow: RiArrowDownSLine,
  upArrow: RiArrowUpSLine,
  rightArrow: RiArrowRightSLine,
  leftArrow: RiArrowLeftSLine,
  favoritesFull: IoMdHeart,
  quotes: FaCommentsDollar,
  info: AiFillInfoCircle,
  check: FiCheck,
  location: MdLocationOn,
  dropdownArrow: IoIosArrowDown,
  tooltipCaret: GrCaretUpFill,
  notificationInfo: IoMdInformationCircle,
  empty: AiOutlineContainer,
  errorTriangular: BiSolidError,
  errorCircle: BiErrorCircle,
  plus: AiOutlinePlus,
  minus: AiOutlineMinus,
  warning: RiAlertFill,
  checkCircle: IoMdCheckmarkCircle,
  addCircle: IoMdAddCircle,
  arrowUp: IoIosArrowUp,
  visible: FaEye,
  hidden: FaEyeSlash,
  apple: FaApple,
  google: FaGoogle,
  idCard: FaRegAddressCard,
  removeCircle: IoMdRemoveCircle,
  checkoutCheckCircle: AiFillCheckCircle,
  chevronUpDown: HiChevronUpDown,
  print: AiFillPrinter,
  regCircle: GoCircle,
  regCheckCircle: GoCheckCircleFill,
  map: TbMap,
  play: BsPlayBtn,
  pause: BsPauseBtn,
  dot: TbPointFilled,
  lock: IoIosLock,
  calendar: PiCalendarPlusBold,
  saveLater: PiFolderPlusBold,
  errWarning: RiErrorWarningFill,
  externalLink: HiOutlineExternalLink,
  signout: LuArrowRightFromLine,
  sortedArrow: TiArrowSortedDown,
  yahoo: FaYahoo,
  signIn: FaUserCircle,
};

export interface IconProps extends HTMLAttributes<SVGElement> {
  name: IconName;
  dataAnalytics?: DataAnalytics;
}

export default function Icon({ name, className, dataAnalytics, ...props }: IconProps): JSX.Element {
  const {splunkLogger} = useSplunkLogger()

  const dataAnalyticsLocation = buildAnalyticsLocations(dataAnalytics?.locations,splunkLogger);
  const dataAnalyticsClick = dataAnalytics?.click
    ? dataAnalytics?.click
    : DataAnalyticsClicks.NOT_AVAILABLE;

  // eslint-disable-next-line no-prototype-builtins
  if (!iconMap.hasOwnProperty(name)) {
    return <></>;
  }
  const SelectedIcon = iconMap[name];
  return (
    <SelectedIcon
      data-testid={name}
      data-analytics-type={DataAnalyticsTypes.Media}
      data-analytics-click={dataAnalyticsClick}
      data-analytics-location={dataAnalyticsLocation}
      className={className}
      {...props}
    />
  );
}
